/**
 * Idle Timeout Warning Modal
 * Displays a warning when user is about to be logged out due to inactivity
 */

import React from 'react';
import { formatRemainingTime } from '../../lib/auth/idleTimeoutConfig';

interface IdleTimeoutWarningModalProps {
  isOpen: boolean;
  remainingTime: number;
  onStayLoggedIn: () => void;
  onLogout: () => void;
}

const IdleTimeoutWarningModal: React.FC<IdleTimeoutWarningModalProps> = ({
  isOpen,
  remainingTime,
  onStayLoggedIn,
  onLogout
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
              {/* Warning icon */}
              <svg
                className="h-6 w-6 text-yellow-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                Session Timeout Warning
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  You've been inactive for a while. For your security, you will be automatically logged out in:
                </p>
                <div className="mt-4 text-center">
                  <div className="inline-flex items-center justify-center px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <svg
                      className="h-5 w-5 text-yellow-600 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-2xl font-bold text-yellow-700">
                      {formatRemainingTime(remainingTime)}
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-500">
                  Click "Stay Logged In" to continue your session, or "Logout Now" to sign out immediately.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 flex flex-col-reverse sm:flex-row-reverse gap-3">
            <button
              type="button"
              onClick={onStayLoggedIn}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:w-auto sm:text-sm"
            >
              Stay Logged In
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:w-auto sm:text-sm"
            >
              Logout Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdleTimeoutWarningModal;